import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CursorLabel from './CursorLabel';

const ASSET_BASE = 'https://processed.portal.soot.com/playground-static-assets/sootworldimages';
const FONT_URL = 'https://processed.portal.soot.com/playground-static-assets/ABCDiatypeRoundedPlusVariable-Trial.ttf';

const fontFace = `
  @font-face {
    font-family: 'ABC Diatype Rounded Medium';
    src: url('${FONT_URL}') format('truetype');
    font-weight: 500;
    font-style: normal;
  }
`;

const HeaderText = () => (
  <div className="absolute top-8 left-0 right-0 z-10 flex justify-center">
    <svg className="w-96">
      <text
        x="50%"
        y="30"
        style={{
          fontFamily: "'ABC Diatype Rounded Medium', sans-serif",
          fontSize: "24px",
          letterSpacing: '-0.02em',
          fontWeight: 500,
          fill: '#333333',
          textAnchor: 'middle',
        }}
      >
        Choose your level
      </text>
    </svg>
  </div>
);

const ImageLink = ({
  src,
  className,
  alt,
  titleText,
  link,
  delay = 0,
  onSelect
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => setIsLoaded(true);
    return () => {
      img.onload = null;
    };
  }, [src]);

  return (
    <motion.div 
      className={`${className} cursor-pointer`}
      data-image-link
      data-hover-text={titleText}
      initial={{ opacity: 0, scale: 0.8, y: 30 }}
      animate={{ 
        opacity: isLoaded ? 1 : 0,
        scale: 1,
        y: 0
      }}
      transition={{
        duration: 0.2,
        delay,
        type: "spring",
        stiffness: 60,
        damping: 10
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(link)}
    >
      <motion.div 
        className="w-full h-full"
        animate={{ scale: isHovered ? 1.1 : 1 }}
        transition={{ duration: 0.15 }}
        style={{ zIndex: isHovered ? 50 : 'auto' }}
      >
        <img
          className="w-full h-full object-cover rounded-lg"
          src={src}
          alt={alt}
          style={{
            maskImage: `url(${src})`,
            WebkitMaskImage: `url(${src})`,
            maskSize: '100% 100%',
            WebkitMaskSize: '100% 100%',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat'
          }}
        />
      </motion.div>
    </motion.div>
  );
};

const ImageGallery = ({ onSelect }) => {
  const imageData = [
    {
      className: "w-64 h-64",
      alt: "Fashion World",
      src: `${ASSET_BASE}/image-7.png`,
      titleText: "Fashion World",
      link: "fashion",
      delay: 0
    },
    {
      className: "w-64 h-64",
      alt: "Product World",
      src: `https://processed.portal.soot.com/playground-static-assets/teenage.png`,
      titleText: "Product World",
      link: "product",
      delay: 0.1
    },
    {
      className: "w-64 h-64",
      alt: "Nature World",
      src: `${ASSET_BASE}/image-9.png`,
      titleText: "Nature World",
      link: "nature",
      delay: 0.2
    }
  ];

  return (
    <div className="flex gap-8 justify-center items-center">
      {imageData.map((image) => (
        <ImageLink
          key={image.titleText}
          {...image}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
};

const ChooseTraining = ({ onSelect }) => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen overflow-hidden relative bg-[#e9e9e9]">
      <style>{fontFace}</style>
      <HeaderText />
      
      <div className="relative w-full h-screen flex items-center justify-center">
        <ImageGallery onSelect={onSelect} />
      </div>

      <CursorLabel />
    </div>
  );
};

export default ChooseTraining;